import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../common/Modal';
import { styled } from 'styled-components';
import ModalHeader from '../common/ModalHeader';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../data/store';
import { closeModal } from '../../data/stores/modalStore';
import { fetchTemplateMappings } from '../../data/thunks/templateThunk';
import Button from '../common/buttons/Button';
import { TemplateMapping } from '../../data/models/template';
import BusySpinner from '../common/BusySpinner';

export default function TemplateGoogleFileNotFoundModal() {
	const {
		openId,
		props: { companyTemplate, onSave },
	} = useSelector((state: RootState) => state.modalState);

	const open = openId === 'templateGoogleFileNotFoundModal';
	const [isLoading, setIsLoading] = useState(false);

	const dispatch = useDispatch<AppDispatch>();

	const {
		templateMappings: { list: mappingsList },
	} = useSelector((state: RootState) => state.templateState);

	const {
		company: { googleDrive },
	} = useSelector((state: RootState) => state.companyState);

	const existsInMapping = useMemo(() => {
		const mappings: TemplateMapping[] = [];
		if (mappingsList.length > 0 && companyTemplate) {
			mappingsList.forEach((mapping) => {
				mapping.templates.forEach((mappingTemplate) => {
					if (
						mappingTemplate.activeVersion.googleId ===
						companyTemplate.activeVersion.googleId
					) {
						mappings.push(mapping);
					}
				});
			});
		}
		return mappings;
	}, [mappingsList, companyTemplate]);

	const handleClose = () => {
		dispatch(closeModal());
	};

	const handleDelete = async () => {
		if (onSave && companyTemplate?.id) {
			setIsLoading(true);
			try {
				await onSave(companyTemplate.id);
				handleClose();
			} catch (error) {
				// Error handling is done in the parent component
				console.error('Error deleting template:', error);
			} finally {
				setIsLoading(false);
			}
		}
	};

	const handleOpenTemplatesFolder = () => {
		window.open(
			`https://drive.google.com/drive/u/${googleDrive.driveId}/folders/${googleDrive.templatesFolderId}`,
			'_blank'
		);
	};

	return (
		<Modal open={open} handleClose={handleClose}>
			<Container>
				{isLoading && (
					<SpinnerOverlay>
						<BusySpinner />
					</SpinnerOverlay>
				)}
				<ModalHeader>Google File Not Found</ModalHeader>
				<div>Custom Template:</div>
				<BoldText>{companyTemplate?.name}</BoldText>
				<TextParagraph>
					The associated Google file for this custom template is
					missing from the{' '}
					<ClickableLink onClick={handleOpenTemplatesFolder}>
						Custom Templates folder
					</ClickableLink>
					. As a result, this template cannot be used when creating a
					new project.
				</TextParagraph>
				{existsInMapping.length > 0 && (
					<>
						<TextParagraph>
							This template appears in the following mappings:
						</TextParagraph>
						{existsInMapping.map((mapping) => (
							<BoldText key={mapping.id}>{mapping.name}</BoldText>
						))}
					</>
				)}
				<TextParagraph>
					If this template is not needed, press the delete button
					below. This will remove the template from here and all other
					project types.
				</TextParagraph>
				<SaveCancelContainer>
					<Button
						onClick={handleDelete}
						color="error"
						variant="outlined"
						disabled={isLoading}
					>
						Delete
					</Button>
				</SaveCancelContainer>
			</Container>
		</Modal>
	);
}

const Container = styled.div`
	min-width: 400px;
	position: relative;
`;

const BoldText = styled.div`
	font-weight: 600;
	font-size: 18px;
`;

const TextParagraph = styled.div`
	margin-top: 20px;
`;

const SaveCancelContainer = styled.div`
	margin-top: ${(props) => props.theme.spacing(4)};
`;

const ErrorText = styled.div`
	color: ${(props) => props.theme.palette.error.main};
	margin-bottom: 20px;
	margin-top: 20px;
	font-weight: 600;
`;

const ClickableLink = styled.span`
	color: ${(props) => props.theme.palette.primary.main};
	cursor: pointer;
	text-decoration: underline;

	&:hover {
		text-decoration: none;
	}
`;

const SpinnerOverlay = styled.div`
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	display: flex;
	justify-content: center;
	align-items: center;
	z-index: 1000;
	border-radius: 8px;
`;
